import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { GlassCard } from "@/components/common/GlassCard";
import { StatusPill } from "@/components/common/StatusPill";
import { Sparkles, ArrowUpRight, Clock, Check, Eye, AlertCircle } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, Tooltip as RTooltip } from "recharts";

const AGENT_SHORT = {
  scouting_report: "Scouting",
  draft_strategy: "Draft Strategy",
  team_fit_explanation: "Team Fit",
  interview_evaluator: "Interview",
  interview_summary: "Interview Summary",
  player_360_synthesis: "Player 360°",
  report_draft: "Report",
  player_narrative: "Narrative",
  weekly_progress: "Weekly Progress",
};

const STATUS_TONE = {
  draft: "cyan",
  needs_review: "amber",
  approved: "emerald",
  rejected: "rose",
  published: "violet",
};
const STATUS_LABEL = {
  draft: "Draft",
  needs_review: "Review",
  approved: "Approved",
  rejected: "Rejected",
  published: "Published",
};

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

// Build a 7-day bucket histogram from a list of events.
function buildSparkline(items) {
  const buckets = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - (6 - i));
    return { day: d.toISOString().slice(5, 10), ts: d.getTime(), v: 0 };
  });
  const sevenDays = Date.now() - 7 * 86400 * 1000;
  for (const it of items) {
    const t = new Date(it.created_at || it.updated_at).getTime();
    if (t < sevenDays) continue;
    const idx = buckets.findIndex((b) => t >= b.ts && t < b.ts + 86400 * 1000);
    if (idx >= 0) buckets[idx].v += 1;
  }
  return buckets;
}

export function AIActivityWidget() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ by_status: {}, by_agent: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [list, c] = await Promise.all([
          api.get("/agent-outputs", { params: { limit: 200 } }),
          api.get("/agent-outputs/counts"),
        ]);
        if (cancelled) return;
        setItems(list.data.items || []);
        setCounts(c.data || { by_status: {}, by_agent: {} });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const pending = (counts.by_status?.draft || 0) + (counts.by_status?.needs_review || 0);
  const recent = items.slice(0, 5);
  const spark = buildSparkline(items);

  return (
    <GlassCard className="col-span-12 lg:col-span-7 p-6 overflow-hidden" testId="dashboard-ai-widget">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-cyan-300" />
            <h3 className="text-1 font-semibold">AI Activity</h3>
            <StatusPill tone="neutral">9 agents</StatusPill>
          </div>
          <p className="text-3 text-xs mt-1">Last 7 days — draft, approve, publish pipeline.</p>
        </div>
        <Link to="/app/ai-cockpit" data-testid="dashboard-ai-widget-open" className="text-cyan-300 text-sm hover:underline inline-flex items-center gap-1">
          Open Cockpit <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Mini stat row + sparkline */}
      <div className="mt-5 grid grid-cols-12 gap-4 items-center">
        <div className="col-span-12 md:col-span-4">
          <div className="grid grid-cols-3 gap-2">
            <MiniStat icon={Clock} label="Pending" value={pending} tone="amber" />
            <MiniStat icon={Check} label="Approved" value={counts.by_status?.approved || 0} tone="emerald" />
            <MiniStat icon={Eye} label="Published" value={counts.by_status?.published || 0} tone="violet" />
          </div>
        </div>
        <div className="col-span-12 md:col-span-8 h-24 min-h-[96px] min-w-[200px]">
          <ResponsiveContainer width="100%" height="100%" minHeight={96} minWidth={200}>
            <AreaChart data={spark} margin={{ top: 5, right: 6, left: 6, bottom: 0 }}>
              <defs>
                <linearGradient id="ai-spark" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22D3EE" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#22D3EE" stopOpacity={0} />
                </linearGradient>
              </defs>
              <RTooltip
                contentStyle={{ background: "rgba(10,18,28,0.95)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, color: "white", fontSize: 11 }}
                labelStyle={{ color: "rgba(255,255,255,0.6)" }}
                formatter={(v) => [`${v} events`, ""]}
              />
              <Area type="monotone" dataKey="v" stroke="#22D3EE" strokeWidth={2} fill="url(#ai-spark)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent feed */}
      <div className="mt-4 border-t border-white/5 pt-3 space-y-1.5">
        <div className="text-3 text-[10px] uppercase tracking-wider mb-1">Recent</div>
        {loading && <div className="text-3 text-sm">Loading…</div>}
        {!loading && recent.length === 0 && <div className="text-3 text-sm italic">No AI activity yet — generate a report or synthesis.</div>}
        {recent.map((x) => (
          <Link
            key={x.id}
            to="/app/ai-cockpit"
            data-testid={`dashboard-ai-recent-${x.id}`}
            className="flex items-center gap-3 py-1.5 px-2 -mx-2 rounded-lg hover:bg-white/[0.03] transition"
          >
            <StatusPill tone={STATUS_TONE[x.status] || "neutral"}>{STATUS_LABEL[x.status] || x.status}</StatusPill>
            <div className="text-2 text-xs w-28 truncate shrink-0">{AGENT_SHORT[x.agent_key] || x.agent_key}</div>
            <div className="text-1 text-sm truncate flex-1 min-w-0">{x.summary || "—"}</div>
            {x.error_md && <AlertCircle className="h-3.5 w-3.5 text-rose-300 shrink-0" />}
            <div className="text-3 text-[11px] shrink-0">{timeAgo(x.created_at)}</div>
          </Link>
        ))}
      </div>
    </GlassCard>
  );
}

function MiniStat({ icon: Icon, label, value, tone }) {
  const t = {
    amber: "text-amber-200",
    emerald: "text-emerald-200",
    violet: "text-violet-200",
  }[tone] || "text-white/80";
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5">
      <div className={`flex items-center gap-1 text-[10px] uppercase tracking-wider ${t} opacity-80`}>
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-1 text-2xl font-semibold mt-0.5">{value}</div>
    </div>
  );
}
